// ============================================================================
// Collections Saga
// Uses APP_CONFIG for port/type — no auth state reads for config values.
// ============================================================================

import { call, put, select, takeLatest, takeEvery, all, fork } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import collectionsApi, { createManualCollection } from '../../api/collections';
import { APP_CONFIG } from '../../config/app.config';
import {
  fetchCollectionsRequest,
  fetchCollectionsSuccess,
  fetchCollectionsFailure,
  generateCollectionsRequest,
  generateCollectionsSuccess,
  generateCollectionsFailure,
  saveCollectionRequest,
  saveCollectionSuccess,
  saveCollectionFailure,
  editCollectionRequest,
  editCollectionSuccess,
  editCollectionFailure,
  deleteCollectionRequest,
  deleteCollectionSuccess,
  deleteCollectionFailure,
  addGeneratedBundlesRequest,
  addGeneratedBundleProgress,
  addGeneratedBundlesSuccess,
  addGeneratedBundlesFailure,
  fetchUserCollectionMediaRequest,
  fetchUserCollectionMediaSuccess,
  fetchUserCollectionMediaFailure,
  regenerateImageRequest,
  regenerateImageSuccess,
  regenerateImageFailure,
  regenerateVideoRequest,
  regenerateVideoSuccess,
  regenerateVideoFailure,
  manualCollectionRequest,
} from '../slices/collectionsSlice';
import type { RootState } from '../store';
import type {
  Collection,
  AddBundlePayload,
  GenerateCollectionsPayload,
  GenerateCollectionsResponse,
  ManualCollectionPayload,
  ManualCollectionResponse,
} from '../../types/collections.types';

// ---------------------------------------------------------------------------
// Helper: get auth user id from state
// ---------------------------------------------------------------------------
function* getAuthUserId(): Generator<any, string, any> {
  const authData: any = yield select((state: RootState) => state.auth.data);
  return authData?.message?.id || '';
}

// Dev-only logger
const log = import.meta.env.DEV
  ? (msg: string, ...args: any[]) => console.log(msg, ...args)
  : () => {};

// ---------------------------------------------------------------------------
// Fetch Collections
// ---------------------------------------------------------------------------
function* handleFetchCollections(): Generator<any, void, any> {
  try {
    const userId: string = APP_CONFIG.APPAREL_USER_ID;

    const collections: Collection[] = yield call(collectionsApi.fetchCollections, userId);
    yield put(fetchCollectionsSuccess(collections));
  } catch (error: any) {
    yield put(fetchCollectionsFailure(error.message || 'Failed to fe  collections'));
  }
}

// ---------------------------------------------------------------------------
// Generate Collections (AI)
// ---------------------------------------------------------------------------
function* handleGenerateCollections(action: PayloadAction<GenerateCollectionsPayload>): Generator<any, void, any> {
  try {
    log('🎯 Generate collections:', action.payload);

    const response: GenerateCollectionsResponse = yield call(
      collectionsApi.generateCollections,
      action.payload,
    );

    yield put(generateCollectionsSuccess(response));
    log('🚀 Auto-triggering bundle addition...');
    yield put(addGeneratedBundlesRequest(response));
  } catch (error: any) {
    yield put(generateCollectionsFailure(error.message || 'Failed to generate collections'));
  }
}

// ---------------------------------------------------------------------------
// Save Collection (Add Bundle)
// ---------------------------------------------------------------------------
function* handleSaveCollection(action: PayloadAction<AddBundlePayload>): Generator<any, void, any> {
  try {
    const userId: string = APP_CONFIG.APPAREL_USER_ID;
    const headers = { userId, productType: APP_CONFIG.TYPE };

    const response: any = yield call(collectionsApi.addBundle, action.payload, headers);
    yield put(saveCollectionSuccess(response));
    yield put(fetchCollectionsRequest());
  } catch (error: any) {
    yield put(saveCollectionFailure(error.message || 'Failed to save collection'));
  }
}

// ---------------------------------------------------------------------------
// Edit Collection
// ---------------------------------------------------------------------------
function* handleEditCollection(action: PayloadAction<AddBundlePayload & { pk_id: string }>): Generator<any, void, any> {
  try {
    const userId: string = APP_CONFIG.APPAREL_USER_ID;
    const headers = { userId, productType: APP_CONFIG.TYPE };

    const response: any = yield call(collectionsApi.editBundle, action.payload, headers);
    yield put(editCollectionSuccess(response));
    yield put(fetchCollectionsRequest());
  } catch (error: any) {
    yield put(editCollectionFailure(error.message || 'Failed to edit collection'));
  }
}

// ---------------------------------------------------------------------------
// Delete Collection
// ---------------------------------------------------------------------------
function* handleDeleteCollection(action: PayloadAction<{ pk_id: string }>): Generator<any, void, any> {
  try {
    yield call(collectionsApi.deleteBundle, action.payload.pk_id);
    yield put(deleteCollectionSuccess({ pk_id: action.payload.pk_id }));
  } catch (error: any) {
    yield put(deleteCollectionFailure(error.message || 'Failed to delete collection'));
  }
}

// ---------------------------------------------------------------------------
// Add Generated Bundles (auto-triggered after AI/Manual generation)
// ---------------------------------------------------------------------------
function* handleAddGeneratedBundles(action: PayloadAction<GenerateCollectionsResponse>): Generator<any, void, any> {
  try {
    const { collections } = action.payload;
    const userId: string = APP_CONFIG.APPAREL_USER_ID;
    const headers = { userId, productType: APP_CONFIG.TYPE };

    const themeTitle: string = yield select((s: RootState) => s.collections.themeTitle);
    const themeDescription: string = yield select((s: RootState) => s.collections.themeDescription);
    const generatePayload: any = yield select((s: RootState) => s.collections.generatePayload);

    for (let i = 0; i < collections.length; i++) {
      const col = collections[i];
      const bundlePayload: AddBundlePayload = {
        title: col.collection_name,
        header: themeTitle,
        long_desc: themeDescription || col.collection_description,
        short_desc: col.collection_description,
        image: col.matched_products[0]?.image || '',
        status: 'inactive',
        category: generatePayload?.category || [],
        products_list: col.matched_products,
        products: col.matched_products.map((p: any) => p.sku),
        theme_type: 'mosaic',
        enable_personalization: generatePayload?.enable_personalization ?? false,
        brand_context_enabled: generatePayload?.brand_context_enabled ?? true,
        add_as_a_collection: generatePayload?.add_as_a_collection ?? true,
        id: APP_CONFIG.TYPE,
        theme_description: themeDescription || '',
      };

      try {
        yield call(collectionsApi.addBundle, bundlePayload, headers);
        yield put(addGeneratedBundleProgress(i + 1));
      } catch (e: any) {
        if (import.meta.env.DEV) console.error(`❌ Bundle ${i + 1} failed:`, e);
      }
    }

    yield put(addGeneratedBundlesSuccess());
    yield put(fetchCollectionsRequest());

    // Wait then fetch media status
    yield new Promise(r => setTimeout(r, 1000));
    yield put(fetchUserCollectionMediaRequest({ userId: APP_CONFIG.MEDIA_USER_ID }));
  } catch (error: any) {
    yield put(addGeneratedBundlesFailure(error.message || 'Failed to add bundles'));
  }
}

// ---------------------------------------------------------------------------
// Fetch User Collection Media
// ---------------------------------------------------------------------------
function* handleFetchUserCollectionMedia(): Generator<any, void, any> {
  try {
    const response: any = yield call(
      collectionsApi.fetchUserCollectionMedia,
      APP_CONFIG.MEDIA_USER_ID,
    );

    if (response.success && Array.isArray(response.data)) {
      yield put(fetchUserCollectionMediaSuccess(response.data));
    } else {
      throw new Error('Invalid media response format');
    }
  } catch (error: any) {
    yield put(fetchUserCollectionMediaFailure(error.message || 'Failed to fetch media'));
  }
}

// ---------------------------------------------------------------------------
// Regenerate Image
// ---------------------------------------------------------------------------
function* handleRegenerateImage(
  action: PayloadAction<{ collectionId: string; userId: string; imageUrls: string[] }>,
): Generator<any, void, any> {
  try {
    const { collectionId, userId, imageUrls } = action.payload;
    yield call(collectionsApi.regenerateImage, userId, collectionId, imageUrls);
    yield put(regenerateImageSuccess({ collectionId }));

    yield new Promise(r => setTimeout(r, 2000));
    yield put(fetchUserCollectionMediaRequest({ userId: APP_CONFIG.MEDIA_USER_ID }));
  } catch (error: any) {
    yield put(regenerateImageFailure({ collectionId: action.payload.collectionId, error: error.message }));
  }
}

// ---------------------------------------------------------------------------
// Regenerate Video
// ---------------------------------------------------------------------------
function* handleRegenerateVideo(
  action: PayloadAction<{ collectionId: string; userId: string }>,
): Generator<any, void, any> {
  try {
    const { collectionId, userId } = action.payload;
    yield call(collectionsApi.regenerateVideo, userId, collectionId);
    yield put(regenerateVideoSuccess({ collectionId }));

    yield new Promise(r => setTimeout(r, 2000));
    yield put(fetchUserCollectionMediaRequest({ userId: APP_CONFIG.MEDIA_USER_ID }));
  } catch (error: any) {
    yield put(regenerateVideoFailure({ collectionId: action.payload.collectionId, error: error.message }));
  }
}

// ---------------------------------------------------------------------------
// Manual Collection (maps response → existing bundle flow)
// ---------------------------------------------------------------------------
function mapManualToGenerateResponse(response: ManualCollectionResponse): GenerateCollectionsResponse {
  return {
    strategic_rationale: response.strategic_rationale,
    collections: response.collections.map(col => ({
      collection_name: col.collection_name,
      collection_description: col.collection_description,
      matched_products: col.products.map(p => ({
        sku: p.sku,
        lowercase_title: p.lowercase_title,
        category: p.category,
        image: p.image,
        modified_price: p.modified_price,
        price: String(p.modified_price),
        description: '',
        vendor: '',
        url: '',
        productId: 0,
        type: '',
        categories: p.category,
      })),
    })),
  };
}

function* handleManualCollection(action: PayloadAction<ManualCollectionPayload>): Generator<any, void, any> {
  try {
    log('🎯 Manual collection:', action.payload);
    const response: ManualCollectionResponse = yield call(createManualCollection, action.payload);
    const mapped = mapManualToGenerateResponse(response);

    yield put(generateCollectionsSuccess(mapped));
    yield put(addGeneratedBundlesRequest(mapped));
  } catch (error: any) {
    yield put(generateCollectionsFailure(error.message || 'Failed to create manual collection'));
  }
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------
export function* collectionsSaga() {
  yield all([
    fork(function* () { yield takeLatest(fetchCollectionsRequest.type, handleFetchCollections); }),
    fork(function* () { yield takeLatest(generateCollectionsRequest.type, handleGenerateCollections); }),
    fork(function* () { yield takeLatest(manualCollectionRequest.type, handleManualCollection); }),
    fork(function* () { yield takeLatest(saveCollectionRequest.type, handleSaveCollection); }),
    fork(function* () { yield takeLatest(editCollectionRequest.type, handleEditCollection); }),
    fork(function* () { yield takeLatest(deleteCollectionRequest.type, handleDeleteCollection); }),
    fork(function* () { yield takeEvery(addGeneratedBundlesRequest.type, handleAddGeneratedBundles); }),
    fork(function* () { yield takeLatest(fetchUserCollectionMediaRequest.type, handleFetchUserCollectionMedia); }),
    fork(function* () { yield takeEvery(regenerateImageRequest.type, handleRegenerateImage); }),
    fork(function* () { yield takeEvery(regenerateVideoRequest.type, handleRegenerateVideo); }),
  ]);
}

export default collectionsSaga;
